import { Box, FormLabel, Grid } from "@mui/material";
import { TextFieldProps as MaterialTextFieldProps } from "@mui/material/TextField";
import { FieldMetaProps } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import { PatternFormat } from "react-number-format";
import { FieldError } from "./FieldError";
import { OCFormControl } from "./FormControl";
import { OCTextField } from "./TextField";
interface IPhoneProps {
  InputProps?: any; // <- material input wrapper component
  inputProps?: any; // <- actual input element
  className?: string;
  onChangeDelay?: number;
  meta?: Partial<FieldMetaProps<any>>;
  id?: string; // Override use of input name for id assigned.
  noCountryCode?: boolean; // Determined is mask includes country code or not "+9 (999) 999-9999"
  noMask?: boolean; // Do not use phone number mask.
  labelSX?: any;
}

export type IPhoneFieldProps = IPhoneProps & MaterialTextFieldProps;

// TEXTFIELD COMPONENT
export const PhoneField = (props: IPhoneFieldProps): JSX.Element => {
  const {
    label,
    name,
    onFocus,
    onBlur,
    onChange,
    value,
    onChangeDelay,
    InputProps,
    className,
    disabled,
    meta,
    id,
    noCountryCode,
    noMask,
    labelSX,
  } = props;

  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [typingTimer, setTypingTimer] = useState<any>(null);
  const [internalValue, setInternalValue] = useState<string | number>("");

  // Track if formik value is dirty.
  const dirty: boolean = useMemo(() => {
    if (!meta) return false;
    const { initialValue, touched } = meta;
    return touched && value !== initialValue ? true : false;
  }, [meta, value]);

  // Clear timer on unmount of component.
  useEffect(() => () => clearTimeout(typingTimer), [typingTimer]);

  // If value is changed from outside, update internal controlled value
  useEffect(() => {
    // Debug.log('outside change: ', value)
    const newValue = value === undefined || value === null ? "" : value;
    setInternalValue(newValue as string | number);
  }, [value]);

  const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    setIsFocused(true);
    // Uses props onfocus (as formik doesn't supply one)
    if (onFocus && typeof onFocus === "function") {
      onFocus(e);
    }
  };

  // Custom blur handler for bug workaround due to input value
  // resetting when using search feature onBlur.
  const handleBlur = (e: React.FocusEvent<Element>): void => {
    e.persist();
    setIsFocused(false);
    setTimeout(() => {
      if (onBlur && typeof onBlur === "function") {
        onBlur(e as React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>);
      }
    }, 1);
  };

  // If value is modified by the input component, update the parent controller after a slight delay.
  const handleInputOnchange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    if (!event.target) {
      return;
    }
    const delay = onChangeDelay !== undefined ? onChangeDelay : 2000;
    event.persist();
    // Debug.log('inside change: ', event.target.value)
    setInternalValue(event.target.value);
    // Reset timer onChange and restart it.
    if (typingTimer) clearTimeout(typingTimer);
    setTypingTimer(
      setTimeout(() => {
        // Fire off the supplied onChange from props and not the formik field one (it breaks as it wants an event and not a value)
        if (onChange && typeof onChange === "function") {
          onChange(event);
        }
      }, delay)
    );
  };

  const hasError = meta?.error ? true : false;

  return (
    <OCFormControl error={hasError} sx={{ width: "100%", my: 0.3 }}>
      <Grid container sx={{ display: "flex", alignItems: "start" }}>
        <Grid
          item
          sx={{
            display: "flex",
            justifyContent: hasError ? "space-between" : "flex-end",
          }}
          xs
        >
          <FieldError meta={meta} icon={true} />
          {!!label && <FormLabel sx={{ ...labelSX }}>{label}</FormLabel>}
        </Grid>
        <Grid item xs={7}>
          {noMask ? (
            <OCTextField
              name={name}
              className="input_control"
              inputProps={{ id: id ? id : name ? name + "_input" : "", ...props.inputProps }}
              fullWidth={props.fullWidth ?? true}
              placeholder={props.placeholder}
              type="tel"
              onChange={handleInputOnchange}
              onBlur={handleBlur}
              onFocus={handleFocus}
              value={internalValue}
              {...InputProps}
            />
          ) : (
            <Box className="input_control">
              <PatternFormat
                allowEmptyFormatting={true}
                format={noCountryCode ? "(###) ###-####" : "+# (###) ###-####"}
                value={internalValue}
                id={"id-" + name + "-mask"}
                onChange={(event) => handleInputOnchange(event)}
                onBlur={(event: any) => {
                  setTimeout(() => {
                    handleInputOnchange(event);
                    if (onBlur) onBlur(event);
                  }, 0);
                }}
                disabled={disabled}
                name={name}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                placeholder={props.placeholder}
                customInput={OCTextField}
              />
            </Box>
          )}
        </Grid>
      </Grid>
    </OCFormControl>
  );
};
